// src/app/AuthInitializer.tsx

import {
  useEffect,
  useState,
} from 'react';

import type {
  PropsWithChildren,
} from 'react';

import {
  useDispatch,
} from 'react-redux';

import {
  USER_ENDPOINTS,
} from '../api';

import api, {
  refreshAccessToken,
} from '../api/axios';

import {
  store,
  type AppDispatch,
} from './store';

import {
  clearTokens,
  setUser,
} from '../features/auth/authSlice';


export default function AuthInitializer({
  children,
}: PropsWithChildren): React.JSX.Element {

  const dispatch =
    useDispatch<AppDispatch>();


  const [
    initialized,
    setInitialized,
  ] =
    useState(
      false,
    );


  useEffect(
    () => {

      let mounted =
        true;


      const initialize =
        async (): Promise<void> => {

          const {
            accessToken,
            refreshToken,
          } =
            store
              .getState()
              .auth;


          /**
           * =====================================================
           * 완전 비로그인
           *
           * Access Token 없음
           * Refresh Token 없음
           *
           * 인증 복구 필요 없음
           * =====================================================
           */
          if (
            !accessToken &&
            !refreshToken
          ) {

            if (
              mounted
            ) {

              setInitialized(
                true,
              );
            }


            return;
          }


          try {

            /**
             * =================================================
             * 새로고침 / 브라우저 재실행
             *
             * Access Token 없음
             * Refresh Token 있음
             *
             * Access Token 재발급
             * =================================================
             */
            if (
              !accessToken &&
              refreshToken
            ) {

              const tokenData =
                await refreshAccessToken();


              if (
                !tokenData
                  ?.accessToken
              ) {

                throw new Error(
                  '로그인 정보를 갱신할 수 없습니다.',
                );
              }
            }


            /**
             * =================================================
             * 로그인 사용자 정보 복원
             *
             * GET /api/v2/users/me
             * =================================================
             */
            const response =
              await api.get(
                USER_ENDPOINTS.ME,
              );


            if (
              !mounted
            ) {
              return;
            }


            const userData =
              response.data
                ?.data;


            if (
              !userData
            ) {

              throw new Error(
                '사용자 정보를 확인할 수 없습니다.',
              );
            }


            /**
             * =================================================
             * Redux 사용자 복원
             * =================================================
             */
            dispatch(
              setUser(
                userData,
              ),
            );

          } catch (
            error
          ) {

            console.error(
              '[AuthInitializer] 인증 초기화 실패:',
              error,
            );


            /**
             * =================================================
             * 자동 로그인 실패
             *
             * Refresh Token 만료
             * Redis Refresh Token 없음
             * 계정 비활성
             * 사용자 정보 조회 실패
             *
             * 모두 로그아웃 상태로 정리
             * =================================================
             */
            if (
              mounted
            ) {

              dispatch(
                clearTokens(),
              );
            }

          } finally {

            /**
             * =================================================
             * 중요
             *
             * 성공하든 실패하든
             * 반드시 초기화를 종료해야
             * Router가 렌더링됩니다.
             * =================================================
             */
            if (
              mounted
            ) {

              setInitialized(
                true,
              );
            }
          }
        };


      void initialize();


      return () => {

        mounted =
          false;
      };

    },
    [
      dispatch,
    ],
  );


  /**
   * =========================================================
   * 인증 복구 중
   * =========================================================
   */
  if (
    !initialized
  ) {

    return (
      <div
        className="
          flex
          min-h-screen
          items-center
          justify-center
          bg-white
        "
      >

        <div
          className="
            flex
            flex-col
            items-center
            gap-3
          "
        >

          <span
            className="
              h-8
              w-8
              animate-spin
              rounded-full
              border-4
              border-gray-200
              border-t-main
            "
            aria-hidden="true"
          />


          <p
            className="
              text-sm
              text-gray-500
            "
          >
            사용자 정보를 확인하고 있습니다.
          </p>

        </div>

      </div>
    );
  }


  return (
    <>
      {children}
    </>
  );
}